import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CalendarDays, Clock, Phone, MapPinned } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DirectionsMenu } from "@/components/DirectionsMenu";
import { CATEGORY_MAP, fmtDate } from "@/lib/categories";
import { api, errorMessage } from "@/lib/api";

export default function SharedTrip() {
  const { token } = useParams();
  const [trip, setTrip] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => { api.get(`/share/${token}`).then((r) => setTrip(r.data)).catch((e) => setError(errorMessage(e))); }, [token]);

  const days = useMemo(() => {
    const g = {};
    (trip?.items || []).forEach((i) => { (g[i.day] = g[i.day] || []).push(i); });
    return Object.entries(g).sort(([a], [b]) => a.localeCompare(b));
  }, [trip]);

  if (error) return <main className="max-w-xl mx-auto px-4 py-24 text-center text-slate-400" data-testid="shared-trip-error">{error}</main>;
  if (!trip) return <main className="min-h-[60vh]" />;

  return (
    <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12" data-testid="shared-trip">
      <p className="text-xs uppercase tracking-[0.2em] text-[#00E5A3]">Shared trip</p>
      <h1 className="text-4xl font-extrabold tracking-tight mt-1">{trip.owner_name}'s vacation plan</h1>
      {days.length === 0 && <p className="mt-10 text-slate-400"><MapPinned className="inline h-4 w-4 mr-1" /> Nothing planned yet.</p>}
      <div className="mt-10 space-y-10">
        {days.map(([day, list], i) => (
          <section key={day} data-testid={`shared-day-${day}`}>
            <h2 className="flex items-center gap-2 text-lg font-bold mb-4">
              <span className="font-mono-vv text-xs text-[#FF3B6B]">DAY {i + 1}</span><CalendarDays className="h-4 w-4 text-slate-500" /> {fmtDate(day)}
            </h2>
            <div className="space-y-3">
              {list.map((e) => {
                const l = e.listing, cat = CATEGORY_MAP[l.category] || CATEGORY_MAP.shows;
                return (
                  <div key={e.id} data-testid={`shared-item-${e.id}`} className="rounded-2xl bg-[#151C2C] border border-[#26334D] p-4 flex flex-wrap items-center gap-4">
                    <span className="h-11 w-11 rounded-xl bg-[#FF3B6B]/15 text-[#FF3B6B] grid place-items-center"><cat.icon className="h-5 w-5" /></span>
                    <div className="flex-1 min-w-[180px]">
                      <h3 className="font-semibold">{l.name}</h3>
                      <p className="text-sm text-slate-400 flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{e.time || l.hours || cat.label}</p>
                      <p className="text-xs text-slate-500">{l.address || e.city}</p>
                    </div>
                    <DirectionsMenu item={l} />
                    {l.phone && <Button asChild variant="outline" size="icon" className="rounded-full border-[#26334D] bg-transparent"><a href={`tel:${l.phone.replace(/[^\d+]/g, "")}`}><Phone className="h-4 w-4" /></a></Button>}
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>
      <div className="mt-14 rounded-3xl border border-[#26334D] bg-[#151C2C] p-8 text-center">
        <p className="text-lg font-semibold">Planning your own getaway?</p>
        <Link to="/register" data-testid="shared-cta" className="inline-block mt-4 px-6 py-3 rounded-full bg-[#FF3B6B] font-semibold">Try VacaVibe free for 7 days</Link>
      </div>
    </main>
  );
}
